import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";

function DirIcon({ direction }) {
  if (direction === "up") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (direction === "down") return <TrendingDown className="w-4 h-4 text-rose-600" />;
  return <Minus className="w-4 h-4 text-neutral-400" />;
}

function Spark({ series, color }) {
  const data = (series || []).map((p) => ({ v: p.value }));
  if (data.length < 2) return <div className="h-8" />;
  return (
    <div className="h-8 w-full min-w-[40px]">
      <ResponsiveContainer width="99%" height="100%">
        <LineChart data={data}>
          <Line type="monotone" dataKey="v" stroke={color || "#F6CFBE"} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function EntryCard({ e, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`trend-${e.canonical_type}`}
      className="w-full text-left bg-white rounded-2xl border border-neutral-200 p-4 hover:border-neutral-300"
    >
      <div className="flex items-baseline justify-between">
        <div className="font-semibold text-neutral-900">{e.label}</div>
        <div className="text-xs text-neutral-500">{e.period_label}</div>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-semibold">{e.latest_value}</div>
        <div className="text-sm text-neutral-500">{e.unit}</div>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-xs text-neutral-600">
        <DirIcon direction={e.direction} />
        <span>{e.change_text || `${e.count} readings`}</span>
      </div>
      <div className="mt-3">
        <Spark series={e.series} color={e.color} />
      </div>
    </button>
  );
}

export default function Trends() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/trends/overview");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 w-full" /></div>;

  const groups = data?.groups || [];
  const tabs = [{ key: "all", label: "All" }, ...groups.map((g) => ({ key: g.key, label: g.label }))];
  const visibleGroups = activeGroup === "all" ? groups : groups.filter((g) => g.key === activeGroup);

  return (
    <div className="space-y-6" data-testid="trends-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Trends</h1>
        <p className="text-neutral-600 mt-1 text-sm">A library of your measurements — {data?.total || 0} in total.</p>
      </div>

      {data?.highlights?.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">What&apos;s changing</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {data.highlights.map((h, i) => (
              <button
                key={i}
                onClick={() => navigate(`/observation/${h.canonical_type}`)}
                data-testid={`highlight-${h.canonical_type}`}
                className="text-left bg-white rounded-2xl border border-neutral-200 p-4 hover:border-neutral-300"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{h.label}</div>
                  <DirIcon direction={h.direction} />
                </div>
                <div className="text-xs text-neutral-600 mt-1">{h.change_text}</div>
                <div className="text-lg font-semibold mt-1">{h.pct > 0 ? "+" : ""}{h.pct}%</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2 border-b border-neutral-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveGroup(t.key)}
            data-testid={`tab-${t.key}`}
            className={
              "px-3 py-1.5 rounded-full text-sm " +
              (activeGroup === t.key ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {visibleGroups.length === 0 && (
        <div className="text-neutral-500 text-sm py-10 text-center">No trends yet. Upload a lab or measurement.</div>
      )}

      {visibleGroups.map((g) => (
        <section key={g.key}>
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">{g.label}</h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {g.entries.map((e) => (
              <EntryCard key={e.canonical_type} e={e} onClick={() => navigate(`/observation/${e.canonical_type}`)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
